import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent, HTMLAttributes } from 'react';
import { Fragment, forwardRef, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobArchitectureItemStatistics } from '~/shared/data-contract/job-architecture';
import type { JobArchitectureProfileReviewer } from '~/shared/data-contract/job-profile';
import { IconLevel, IconPeopleAdd, IconRole, PersonCircleIcon } from '~/ui/core/icons';
import {
    calculateStatsProgress,
    StatsCount,
    StatsProgress,
    StatsRow,
    StatsSeparator,
    statsProgressColors,
} from '~/ui/core/stats/Stats';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { DraftBadge } from './DraftBadge';
import { NewVersionBadge } from './NewVersionBadge';
import { PublishedBadge } from './PublishedBadge';
import { ReviewedBadge } from './ReviewedBadge';

const Container = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    fontVariantNumeric: 'tabular-nums',
    gap: theme.spacing.s,
}));

export const ReviewerBadge = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    ({ children, className, ...props }, ref) => (
        <div
            ref={ref}
            className={clsx(
                'flex h-3xl w-3xl shrink-0 cursor-default items-center justify-center rounded-full text-xxxs font-semibold [&_svg]:h-l [&_svg]:w-l',
                'bg-reviewer-badge-surface text-reviewer-badge-text in-data-[is-active=true]:!bg-reviewer-badge-active-surface in-data-[is-active=true]:!text-reviewer-badge-active-text',
                className,
            )}
            {...props}
        >
            {children}
        </div>
    ),
);

ReviewerBadge.displayName = 'ReviewerBadge';

const ChildrenStats: FunctionComponent<{
    readonly approvedChildrenCount: number;
    readonly assignedReviewersCount: number;
    readonly childCount: number;
    readonly publishedChildren?: number;
}> = ({ approvedChildrenCount, assignedReviewersCount, childCount, publishedChildren }) => {
    const { translate } = useContext(ApplicationContext);
    const childProgress = calculateStatsProgress(approvedChildrenCount, childCount);

    return (
        <StatsRow className="flex-nowrap">
            <StatsCount
                count={childCount}
                data-testid="child-levels-count"
                tooltip={translate('pages.job-architecture-v2.statistics.level-count')}
            >
                <IconLevel size={12} />
            </StatsCount>
            <StatsCount
                count={assignedReviewersCount}
                data-testid="child-reviewers-count"
                tooltip={translate('pages.job-architecture-v2.statistics.levels-with-reviewers')}
            >
                <PersonCircleIcon />
            </StatsCount>
            <StatsProgress
                colorBase={statsProgressColors.info.base}
                colorFill={statsProgressColors.info.fill}
                count={approvedChildrenCount}
                data-testid="child-approved-count"
                icon="review"
                progress={childProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.reviewed-levels')}
            />
            {is.positiveNumber(publishedChildren) && (
                <StatsProgress
                    colorBase={statsProgressColors.success.base}
                    colorFill={statsProgressColors.success.fill}
                    count={publishedChildren}
                    data-testid="child-published-count"
                    icon="publish"
                    progress={childProgress}
                    tooltip={translate('pages.job-architecture-v2.statistics.published-levels')}
                />
            )}
        </StatsRow>
    );
};

const getInitial = (profile: { readonly reviewer?: JobArchitectureProfileReviewer }): string => {
    return (profile.reviewer?.firstName?.[0] ?? '') + (profile.reviewer?.lastName?.[0] ?? '');
};

export const Initials: FunctionComponent<{
    readonly profile: { readonly reviewer?: JobArchitectureProfileReviewer };
}> = ({ profile }) => {
    return profile.reviewer !== undefined ? (
        <span>{getInitial(profile)}</span>
    ) : (
        <IconPeopleAdd data-testid="reviewer-add-icon" />
    );
};

const ReviewStateBadge: FunctionComponent<{ readonly isApproved: boolean }> = ({ isApproved }) => {
    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={isApproved ? 'reviewed' : 'draft'}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                initial={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
            >
                {isApproved ? <ReviewedBadge /> : <DraftBadge />}
            </motion.div>
        </AnimatePresence>
    );
};

const ProfileChildrenSection: FunctionComponent<{
    readonly statistics: JobArchitectureItemStatistics;
}> = ({ statistics }) => {
    const hasChildren = is.positiveNumber(statistics.levelsCount);

    if (!hasChildren) {
        return null;
    }

    return (
        <Fragment>
            <StatsSeparator />
            <ChildrenStats
                approvedChildrenCount={statistics.approvedLevels ?? 0}
                assignedReviewersCount={statistics.reviewerLevels ?? 0}
                childCount={statistics.levelsCount ?? 0}
                publishedChildren={statistics.publishedLevels ?? 0}
            />
        </Fragment>
    );
};

export const ProfileStatus: FunctionComponent<{
    readonly isApproved: boolean;
    readonly profile: { readonly reviewer?: JobArchitectureProfileReviewer };
    readonly published?: boolean;
    readonly statistics: JobArchitectureItemStatistics;
}> = ({ isApproved, profile, published, statistics }) => {
    const isPublished = is.boolean(published) && published;

    return (
        <Container data-testid="profile-status">
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <ReviewerBadge data-testid="reviewer-badge">
                        <Initials profile={profile} />
                    </ReviewerBadge>
                </Tooltip.Trigger>
                {profile.reviewer !== undefined && (
                    <Tooltip.Content side="top">
                        {profile.reviewer?.firstName} {profile.reviewer?.lastName}
                    </Tooltip.Content>
                )}
            </Tooltip>
            <StatsSeparator />
            {(!isPublished || isApproved) && <ReviewStateBadge isApproved={isApproved} />}
            {isPublished && <PublishedBadge />}
            {!isApproved && isPublished && <NewVersionBadge />}
            <ProfileChildrenSection statistics={statistics} />
        </Container>
    );
};

export const Status: FunctionComponent<{
    readonly statistics: JobArchitectureItemStatistics;
}> = ({ statistics }) => {
    const { translate } = useContext(ApplicationContext);

    const rolesCount = statistics.rolesCount;
    const levelsCount = statistics.levelsCount;
    const approvedRoleProgress = calculateStatsProgress(statistics.approvedRoles, rolesCount);
    const publishedRoleProgress = calculateStatsProgress(statistics.publishedRoles, rolesCount);
    const assignedRoleProgress = calculateStatsProgress(statistics.reviewerRoles, rolesCount);

    const hasRolesSection = rolesCount > 0;
    const hasLevelsSection = levelsCount > 0;

    return (
        <Container data-testid="status">
            {hasRolesSection && (
                <StatsCount
                    count={rolesCount}
                    data-testid="roles-count"
                    tooltip={translate('pages.job-architecture-v2.statistics.role-count')}
                >
                    <IconRole size={12} />
                </StatsCount>
            )}
            <StatsProgress
                colorBase={statsProgressColors.gray.base}
                colorFill={statsProgressColors.gray.fill}
                count={statistics.reviewerRoles}
                data-testid="reviewer-roles-count"
                icon="count"
                progress={assignedRoleProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.roles-with-reviewers')}
            />
            <StatsProgress
                colorBase={statsProgressColors.info.base}
                colorFill={statsProgressColors.info.fill}
                count={statistics.approvedRoles}
                data-testid="approved-roles-count"
                icon="review"
                progress={approvedRoleProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.reviewed-roles')}
            />
            <StatsProgress
                colorBase={statsProgressColors.success.base}
                colorFill={statsProgressColors.success.fill}
                count={statistics.publishedRoles}
                data-testid="published-roles-count"
                icon="publish"
                progress={publishedRoleProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.published-roles')}
            />
            {hasRolesSection && hasLevelsSection && <StatsSeparator />}
            {hasLevelsSection && (
                <StatsCount
                    count={levelsCount}
                    data-testid="levels-count"
                    tooltip={translate('pages.job-architecture-v2.statistics.level-count')}
                >
                    <IconLevel size={12} />
                </StatsCount>
            )}
        </Container>
    );
};
